/**
 * JIRA/HubSpot Style Opportunity Detail Panel
 * Slide-out panel for opportunity details with tabs
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../api/client';
import {
  X, Calendar, Package, Globe, TrendingUp, Clock, User, Users,
  Edit2, Save, ChevronRight, Activity, FileText, MessageSquare,
  Eye, Plus, Trash2, MoreHorizontal, Link2, Database, ChevronDown, ChevronUp, Layers,
  AlertTriangle, DollarSign
} from 'lucide-react';
import {
  getOpportunity,
  updateOpportunity,
  promoteToProgram,
  changeStage,
  archiveOpportunity,
  formatCompactCurrency,
  getStageDisplayName,
  searchUsersForWatching 
} from '../../api/pipeline';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Separator } from '../ui/separator';
import { ScrollArea } from '../ui/scroll-area';
import { Checkbox } from '../ui/checkbox';
import { Alert, AlertDescription } from '../ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../ui/command';
import { 
  addWatcher, 
  removeWatcher,
  getWatchers 
} from '../../api/pipeline';
import { toast } from 'sonner';
import OpportunityParts from './OpportunityParts';
import OpportunityDocuments from './OpportunityDocuments';
import OpportunityComments from './OpportunityComments';
import OpportunitySummaryDashboard from './OpportunitySummaryDashboard';
import OpportunityContacts from './OpportunityContacts';
import OpportunityPlanningTab from './OpportunityPlanningTab';
import CustomFieldsSection from '../CustomFieldsSection';
import LinkedIHSCarlines from '../ihs/LinkedIHSCarlines';
import IHSCarlineBrowser from '../ihs/IHSCarlineBrowser';
import EntityWorkflowsPanel from '../workflows/EntityWorkflowsPanel';
import ConfirmActionDialog from '../workspace/ConfirmActionDialog';

// Stage options
const STAGES = [
  { id: 'target', name: 'Target', color: 'bg-blue-500' },
  { id: 'qualified', name: 'Qualified', color: 'bg-purple-500' },
  { id: 'rfq', name: 'RFQ', color: 'bg-indigo-500' },
  { id: 'quoted', name: 'Quoted', color: 'bg-cyan-500' },
  { id: 'awarded', name: 'Awarded', color: 'bg-emerald-500' },
  { id: 'sourced', name: 'Sourced', color: 'bg-green-500' },
  { id: 'lost', name: 'Lost', color: 'bg-slate-400' },
];

const OpportunityDetailPanel = ({
  opportunityId,
  isOpen,
  onClose,
  onUpdate
}) => {
  const navigate = useNavigate();
  const [opportunity, setOpportunity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editedData, setEditedData] = useState({});
  const [activeTab, setActiveTab] = useState('summary');
  const [saving, setSaving] = useState(false);
  const [showIHSBrowser, setShowIHSBrowser] = useState(false);
  const [partTypesExpanded, setPartTypesExpanded] = useState(false);
  const [partsMismatchData, setPartsMismatchData] = useState({ mismatch_count: 0, expected_part_status: '' });
  const [promotingProgram, setPromotingProgram] = useState(false);
  const [promoteConfirmOpen, setPromoteConfirmOpen] = useState(false);
  
  // State for user assignment and watchers
  const [assigneePopoverOpen, setAssigneePopoverOpen] = useState(false);
  const [watcherPopoverOpen, setWatcherPopoverOpen] = useState(false);
  const [watchersState, setWatchersState] = useState([]);
  const [userSearchQuery, setUserSearchQuery] = useState('');

  // Fetch master part types list from Competitive Intelligence module
  const { data: masterPartTypes = [] } = useQuery({
    queryKey: ['masterPartTypes'],
    queryFn: async () => {
      const response = await apiClient.get('/api/competitive-intelligence/part-types?is_active=true&include_pricing=false');
      return response.data || [];
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Fetch available users for watcher/assignment
  const { data: availableUsersResponse, isLoading: usersLoading } = useQuery({
    queryKey: ['availableUsers', opportunityId],
    queryFn: () => searchUsersForWatching(opportunityId, '', 50),
    enabled: (assigneePopoverOpen || watcherPopoverOpen) && !!opportunityId
  });
  const availableUsers = availableUsersResponse?.data || [];

  // Filter users by search query - applies to both assignment and watcher dropdowns
  const filteredUsers = useMemo(() => {
    if (!userSearchQuery.trim()) return availableUsers;
    const query = userSearchQuery.toLowerCase();
    return availableUsers.filter(user => 
      user.name?.toLowerCase().includes(query) ||
      user.email?.toLowerCase().includes(query)
    );
  }, [availableUsers, userSearchQuery]);

  // Add watcher mutation
  const addWatcherMutation = useMutation({
    mutationFn: (userId) => addWatcher(opportunityId, userId),
    onSuccess: (response) => {
      loadWatchers(); // Refresh watchers list
      setWatcherPopoverOpen(false);
      setUserSearchQuery('');
      toast.success('Watcher added successfully');
      if (onUpdate) onUpdate();
    },
    onError: (error) => {
      console.error('Error adding watcher:', error);
      toast.error('Failed to add watcher');
    }
  });

  // Remove watcher mutation
  const removeWatcherMutation = useMutation({
    mutationFn: (watcherId) => removeWatcher(opportunityId, watcherId),
    onSuccess: () => {
      loadWatchers(); // Refresh watchers list
      toast.success('Watcher removed');
      if (onUpdate) onUpdate();
    },
    onError: (error) => {
      console.error('Error removing watcher:', error);
      toast.error('Failed to remove watcher');
    }
  });

  // Fetch parts mismatch data for warning banner
  const fetchPartsMismatchData = async () => {
    if (!opportunityId) return;
    try {
      const response = await apiClient.get(`/api/v1/pipeline/opportunities/${opportunityId}/parts`);
      if (response.data.success) {
        setPartsMismatchData({
          mismatch_count: response.data.mismatch_count || 0,
          expected_part_status: response.data.expected_part_status || '',
          total_parts: response.data.total_parts || 0
        });
      }
    } catch (error) {
      console.error('Error fetching parts mismatch data:', error);
    }
  };

  // Load opportunity details
  useEffect(() => {
    if (isOpen && opportunityId) {
      loadOpportunity();
      fetchPartsMismatchData();
      loadWatchers();
    } else {
      setOpportunity(null);
      setActiveTab('summary');
      setPartsMismatchData({ mismatch_count: 0, expected_part_status: '' });
      setWatchersState([]);
    }
  }, [isOpen, opportunityId]);

  // Load watchers for the opportunity
  const loadWatchers = async () => {
    if (!opportunityId) return;
    try {
      const response = await getWatchers(opportunityId);
      if (response.success) {
        setWatchersState(response.data || []);
      }
    } catch (error) {
      console.error('Error loading watchers:', error);
    }
  };

  // Handle unassigning a user
  const handleUnassignUser = async () => {
    try {
      setSaving(true);
      const response = await updateOpportunity(opportunityId, {
        assigned_to: null
      });
      if (response.success) {
        setOpportunity(response.data);
        setEditedData(response.data);
        setAssigneePopoverOpen(false);
        toast.success('Responsible user cleared');
        if (onUpdate) onUpdate();
      }
    } catch (error) {
      console.error('Error unassigning user:', error);
      toast.error('Failed to unassign user');
    } finally {
      setSaving(false);
    }
  };

  // Note: handleAddWatcher and handleRemoveWatcher use mutations defined later in component

  const loadOpportunity = async () => {
    try {
      setLoading(true);
      const response = await getOpportunity(opportunityId);
      if (response.success) {
        setOpportunity(response.data);
        setEditedData(response.data);
      }
    } catch (error) {
      console.error('Error loading opportunity:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      // Transform editedData to match backend API expectations
      const dataToSend = {
        ...editedData,
        // Convert assigned_to object to just the ID (string)
        assigned_to: editedData.assigned_to?.id || editedData.assigned_to || null,
      };
      // Remove fields that shouldn't be sent to the backend
      delete dataToSend.activities;
      delete dataToSend.created_at;
      delete dataToSend.updated_at;
      delete dataToSend.last_activity_at;
      delete dataToSend.linked_parts_count;
      delete dataToSend.program_id;
      delete dataToSend.canonical_program_name;
      delete dataToSend.program_context_source;
      delete dataToSend.watchers; // Remove if it's an array of objects
      
      const response = await updateOpportunity(opportunityId, dataToSend);
      if (response.success) {
        setOpportunity(response.data);
        setEditing(false);
        setPartTypesExpanded(false); // Collapse part types selection on save
        if (onUpdate) onUpdate();
      }
    } catch (error) {
      console.error('Error saving:', error);
      alert('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleStageChange = async (newStage) => {
    try {
      const response = await changeStage(opportunityId, newStage);
      if (response.success) {
        setOpportunity(response.data);
        if (onUpdate) onUpdate();
      }
    } catch (error) {
      console.error('Error changing stage:', error);
      alert('Failed to change stage');
    }
  };

  const handleArchive = async () => {
    if (window.confirm('Archive this opportunity?')) {
      try {
        await archiveOpportunity(opportunityId);
        if (onUpdate) onUpdate();
        onClose();
      } catch (error) {
        console.error('Error archiving:', error);
        alert('Failed to archive');
      }
    }
  };

  const handleOpenProgram360 = () => {
    if (!opportunity?.program_id) return;
    onClose?.();
    navigate(`/programs/${opportunity.program_id}`);
  };

  const requestPromoteToProgram = () => {
    setPromoteConfirmOpen(true);
  };

  const handlePromoteToProgram = async () => {
    if (!opportunityId) return;
    try {
      setPromotingProgram(true);
      const response = await promoteToProgram(opportunityId);
      if (response.success) {
        const updatedOpportunity = response.data;
        setOpportunity(updatedOpportunity);
        setEditedData(updatedOpportunity);
        toast.success(response.message || 'Program 360 connection updated');
        if (onUpdate) onUpdate();
        const route = response.program?.route || (response.program?.id ? `/programs/${response.program.id}` : null);
        if (route) {
          onClose?.();
          navigate(route);
        }
      }
    } catch (error) {
      console.error('Error creating Program 360:', error);
      toast.error(error.response?.data?.detail || 'Failed to create Program 360');
    } finally {
      setPromotingProgram(false);
    }
  };

  // Handle assigning a user as responsible
  const handleAssignUser = async (user) => {
    try {
      setSaving(true);
      // Extract user ID - handle both user object and direct ID string
      const userId = typeof user === 'object' ? user.id : user;
      const response = await updateOpportunity(opportunityId, {
        assigned_to: userId
      });
      if (response.success) {
        setOpportunity(response.data);
        setEditedData(response.data);
        setAssigneePopoverOpen(false);
        setUserSearchQuery('');
        toast.success('Responsible user updated');
        if (onUpdate) onUpdate();
      }
    } catch (error) {
      console.error('Error assigning user:', error);
    } finally {
      setSaving(false);
    }
  };

  // Handle unassigning the current user
  const handleUnassign = async () => {
    try {
      setSaving(true);
      const response = await updateOpportunity(opportunityId, {
        assigned_to: null
      });
      if (response.success) {
        setOpportunity(response.data);
        setEditedData(response.data);
        setAssigneePopoverOpen(false);
        if (onUpdate) onUpdate();
      }
    } catch (error) {
      console.error('Error unassigning user:', error);
    } finally {
      setSaving(false);
    }
  };

  // Handle adding a watcher - accepts user object or user ID string
  const handleAddWatcher = (user) => {
    const userId = typeof user === 'object' ? user.id : user;
    addWatcherMutation.mutate(userId);
  };

  // Handle removing a watcher
  const handleRemoveWatcher = (watcherId) => {
    removeWatcherMutation.mutate(watcherId);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Not set';
    return new Date(dateStr).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric' 
    });
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-700 border-red-200';
      case 'medium': return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'low': return 'bg-slate-100 text-slate-700 border-slate-200';
      default: return 'bg-slate-100 text-slate-700 border-slate-200';
    }
  };

  const getStageColor = (stageId) => {
    const stage = STAGES.find(s => s.id === stageId);
    return stage?.color || 'bg-slate-500';
  };

  const programConnection = opportunity?.program_connection || {
    status: opportunity?.program_id ? 'suggested' : 'missing',
    program_id: opportunity?.program_id,
    program_name: opportunity?.canonical_program_name,
    match_source: opportunity?.program_context_source,
    linked_parts_count: opportunity?.linked_parts_count || 0
  };
  const hasProgramConnection = ['connected', 'suggested'].includes(programConnection.status) && programConnection.program_id;
  const isCanonicalProgramConnection = programConnection.status === 'connected';

  return (
    <>
      <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <SheetContent 
          side="right" 
          className="w-full sm:max-w-xl md:max-w-2xl lg:max-w-3xl p-0 overflow-hidden"
        >
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-slate-500">Loading...</div>
            </div>
          ) : opportunity ? (
            <div className="flex flex-col h-full">
              {/* Header */}
              <div className="flex-shrink-0 border-b border-slate-200 bg-white">
                {/* pr-12 keeps the action buttons clear of the Sheet's built-in close (X) in the top-right corner */}
                <div className="p-4 pr-12">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      {/* Breadcrumb */}
                      <div className="flex items-center gap-1 text-xs text-slate-500 mb-2">
                        <span>Pipeline</span>
                        <ChevronRight className="w-3 h-3" />
                        <span>{getStageDisplayName(opportunity.stage)}</span>
                      </div>
                      
                      {/* Title */}
                      <h2 className="text-lg font-semibold text-slate-900 truncate">
                        {opportunity.program_name}
                      </h2>
                      
                      {/* Meta info */}
                      <div className="flex items-center gap-3 mt-2">
                        <Badge variant="outline" className={getPriorityColor(opportunity.priority)}>
                          {opportunity.priority?.toUpperCase()}
                        </Badge>
                        <span className="text-sm text-slate-600">{opportunity.oem}</span>
                        {opportunity.platform_code && (
                          <span className="text-sm text-slate-500">{opportunity.platform_code}</span>
                        )}
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-center gap-3 mr-2">
                      {editing ? (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setEditing(false);
                              setEditedData(opportunity);
                            }}
                          >
                            Cancel
                          </Button>
                          <Button
                            size="sm"
                            onClick={handleSave}
                            disabled={saving}
                            className="gap-1"
                          >
                            <Save className="w-4 h-4" />
                            {saving ? 'Saving...' : 'Save'}
                          </Button>
                        </>
                      ) : (
                        <>
                          {hasProgramConnection ? (
                            <Button
                              size="sm"
                              onClick={isCanonicalProgramConnection ? handleOpenProgram360 : requestPromoteToProgram}
                              disabled={promotingProgram}
                              className="gap-1 bg-blue-700 hover:bg-blue-800"
                            >
                              {isCanonicalProgramConnection ? 'Open Program 360' : promotingProgram ? 'Connecting...' : 'Connect Program 360'}
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              onClick={requestPromoteToProgram}
                              disabled={promotingProgram}
                              className="gap-1 bg-blue-700 hover:bg-blue-800"
                            >
                              <Plus className="w-4 h-4" />
                              {promotingProgram ? 'Creating...' : 'Create Program 360'}
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setEditing(true)}
                            className="gap-1"
                          >
                            <Edit2 className="w-4 h-4" />
                            Edit
                          </Button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="text-slate-500">
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" sideOffset={8}>
                              <DropdownMenuItem 
                                className="text-red-600"
                                onClick={handleArchive}
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Archive
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Stage Selector */}
                <div className="px-4 pb-3">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-slate-500">Stage:</span>
                    <Select 
                      value={opportunity.stage} 
                      onValueChange={handleStageChange}
                      disabled={editing}
                    >
                      <SelectTrigger className="h-8 w-auto gap-2 border-0 bg-transparent hover:bg-slate-100 px-2">
                        <span className={`w-2 h-2 rounded-full ${getStageColor(opportunity.stage)}`} />
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STAGES.map((stage) => (
                          <SelectItem key={stage.id} value={stage.id}>
                            <span className="flex items-center gap-2">
                              <span className={`w-2 h-2 rounded-full ${stage.color}`} />
                              {stage.name}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Program 360 Connection */}
                <div className="px-4 pb-3">
                  <div className={`rounded-lg border p-3 ${
                    isCanonicalProgramConnection
                      ? 'bg-emerald-50 border-emerald-200'
                      : hasProgramConnection
                      ? 'bg-blue-50 border-blue-200'
                      : 'bg-amber-50 border-amber-200'
                  }`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-2 min-w-0">
                        <Link2 className={`w-4 h-4 mt-0.5 ${
                          isCanonicalProgramConnection
                            ? 'text-emerald-600'
                            : hasProgramConnection
                            ? 'text-blue-600'
                            : 'text-amber-600'
                        }`} />
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-slate-900">
                            {isCanonicalProgramConnection
                              ? 'Connected to Program 360'
                              : hasProgramConnection
                              ? 'Suggested Program 360 match'
                              : 'No Program 360 record yet'}
                          </p>
                          <p className="text-xs text-slate-600 mt-0.5">
                            {hasProgramConnection
                              ? `${programConnection.program_name || opportunity.program_name} - ${programConnection.linked_parts_count || 0} linked part${(programConnection.linked_parts_count || 0) === 1 ? '' : 's'}`
                              : 'Create the canonical program record when this pursuit is ready to manage parts, forecasts, S&OP, prices, and claims.'}
                          </p>
                          {hasProgramConnection && !isCanonicalProgramConnection && (
                            <p className="text-xs text-blue-700 mt-1">
                              This match is inferred from {programConnection.match_source === 'program_match' ? 'program name/platform' : 'linked parts'}. Connect it to make the relationship explicit.
                            </p>
                          )}
                        </div>
                      </div>
                      {hasProgramConnection ? (
                        <Button
                          variant={isCanonicalProgramConnection ? 'outline' : 'default'}
                          size="sm"
                          onClick={isCanonicalProgramConnection ? handleOpenProgram360 : requestPromoteToProgram}
                          disabled={promotingProgram}
                          className="shrink-0"
                        >
                          {isCanonicalProgramConnection ? 'Open Program 360' : promotingProgram ? 'Connecting...' : 'Connect'}
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          onClick={requestPromoteToProgram}
                          disabled={promotingProgram}
                          className="shrink-0"
                        >
                          {promotingProgram ? 'Creating...' : 'Create Program 360'}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Status Mismatch Warning Banner */}
                {partsMismatchData.mismatch_count > 0 && (
                  <div className="px-4 pb-3">
                    <Alert variant="warning" className="bg-amber-50 border-amber-200">
                      <AlertTriangle className="h-4 w-4 text-amber-600" />
                      <AlertDescription className="text-amber-800 flex items-center justify-between">
                        <span>
                          This opportunity is <strong>{getStageDisplayName(opportunity.stage)}</strong> but{' '}
                          <strong>{partsMismatchData.mismatch_count}</strong> part{partsMismatchData.mismatch_count > 1 ? 's have' : ' has'} different status{partsMismatchData.mismatch_count > 1 ? 'es' : ''}.
                          {partsMismatchData.expected_part_status && (
                            <span className="text-amber-600"> Expected: {partsMismatchData.expected_part_status}</span>
                          )}
                        </span>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-amber-700 hover:text-amber-900 hover:bg-amber-100 ml-2"
                          onClick={() => setActiveTab('parts')}
                        >
                          Review
                        </Button>
                      </AlertDescription>
                    </Alert>
                  </div>
                )}

                {/* Tabs */}
                <Tabs value={activeTab} onValueChange={setActiveTab} className="px-4">
                  <TabsList className="h-auto p-0 bg-transparent border-b-0 gap-4">
                    {[
                      { id: 'summary', label: 'Summary', icon: TrendingUp },
                      { id: 'details', label: 'Details', icon: FileText },
                      { id: 'parts', label: 'Parts', icon: Package },
                      { id: 'planning', label: 'Planning', icon: Layers },
                      { id: 'contacts', label: 'Contacts', icon: Users },
                      { id: 'documents', label: 'Documents', icon: FileText },
                      { id: 'ihs-data', label: 'Market Data', icon: Database },
                      { id: 'activity', label: 'Activity', icon: Activity },
                      { id: 'comments', label: 'Comments', icon: MessageSquare },
                    ].map((tab) => (
                      <TabsTrigger
                        key={tab.id}
                        value={tab.id}
                        className="pb-3 px-0 rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:text-blue-600 data-[state=active]:shadow-none bg-transparent gap-1.5"
                      >
                        {tab.icon && <tab.icon className="w-4 h-4" />}
                        {tab.label}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                </Tabs>
              </div>

              {/* Content */}
              <ScrollArea className="flex-1">
                <div className="p-4">
                  <Tabs value={activeTab}>
                    {/* Summary Tab - NEW Enhanced Dashboard */}
                    <TabsContent value="summary" className="m-0">
                      <OpportunitySummaryDashboard opportunity={opportunity} />
                    </TabsContent>

                    {/* Planning Tab - Unified Scenario Planning */}
                    <TabsContent value="planning" className="m-0">
                      <OpportunityPlanningTab 
                        opportunityId={opportunityId}
                        opportunity={opportunity}
                        onUpdate={onUpdate}
                      />
                    </TabsContent>

                    {/* Contacts Tab - HubSpot Style Customer Contacts */}
                    <TabsContent value="contacts" className="m-0">
                      <OpportunityContacts opportunityId={opportunityId} />
                    </TabsContent>

                    {/* Details Tab - People, Notes, Competitive Info */}
                    <TabsContent value="details" className="m-0 space-y-6">
                      {/* Responsible & Watchers Section */}
                      <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                        <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                          <Users className="w-4 h-4" />
                          People
                        </h3>

                        <div className="space-y-4">
                          {/* Responsible (Assignee) */}
                          <div>
                            <label className="text-xs font-medium text-slate-500 block mb-2">Responsible</label>
                            <Popover open={assigneePopoverOpen} onOpenChange={(open) => {
                              setAssigneePopoverOpen(open);
                              if (open) {
                                setUserSearchQuery('');
                              }
                            }}>
                              <PopoverTrigger asChild>
                                <button className="flex items-center gap-2 hover:bg-slate-100 p-1 rounded-md transition-colors w-full text-left group">
                                  {opportunity.assigned_to ? (
                                    <>
                                      <Avatar className="w-8 h-8">
                                        <AvatarFallback className="text-xs bg-blue-500 text-white">
                                          {getInitials(opportunity.assigned_to.name)}
                                        </AvatarFallback>
                                      </Avatar>
                                      <div className="flex-1">
                                        <p className="text-sm font-medium text-slate-900">{opportunity.assigned_to.name}</p>
                                        <p className="text-xs text-slate-500">{opportunity.assigned_to.email}</p>
                                      </div>
                                      <Edit2 className="w-4 h-4 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                                    </>
                                  ) : (
                                    <>
                                      <Avatar className="w-8 h-8">
                                        <AvatarFallback className="text-xs bg-slate-200 text-slate-500">?</AvatarFallback>
                                      </Avatar>
                                      <span className="text-sm text-slate-500 flex-1">Unassigned</span>
                                      <Plus className="w-4 h-4 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                                    </>
                                  )}
                                </button>
                              </PopoverTrigger>
                              <PopoverContent className="w-72 p-0" align="start">
                                <Command shouldFilter={false}>
                                  <CommandInput 
                                    placeholder="Search users..." 
                                    value={userSearchQuery}
                                    onValueChange={setUserSearchQuery}
                                  />
                                  <CommandList>
                                    <CommandEmpty>
                                      {usersLoading ? 'Loading users...' : 'No users found.'}
                                    </CommandEmpty>
                                    <CommandGroup heading="Users">
                                      {opportunity.assigned_to && (
                                        <CommandItem
                                          onSelect={handleUnassignUser}
                                          className="text-red-600"
                                        >
                                          <X className="mr-2 h-4 w-4" />
                                          Unassign current user
                                        </CommandItem>
                                      )}
                                      {filteredUsers.map((user) => (
                                        <CommandItem
                                          key={user.id}
                                          onSelect={() => handleAssignUser(user)}
                                          className="flex items-center gap-2"
                                        >
                                          <Avatar className="w-6 h-6">
                                            <AvatarFallback className="text-xs bg-slate-200 text-slate-700">
                                              {getInitials(user.name)}
                                            </AvatarFallback>
                                          </Avatar>
                                          <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium truncate">{user.name}</p>
                                            <p className="text-xs text-slate-500 truncate">{user.email}</p>
                                          </div>
                                          {opportunity.assigned_to?.id === user.id && (
                                            <Badge variant="secondary" className="text-xs">Current</Badge>
                                          )}
                                        </CommandItem>
                                      ))}
                                    </CommandGroup>
                                  </CommandList>
                                </Command>
                              </PopoverContent>
                            </Popover>
                          </div>

                          {/* Watchers */}
                          <div>
                            <label className="text-xs font-medium text-slate-500 block mb-2">
                              Watchers
                              <span className="text-slate-400 font-normal"> - Get notified of changes</span>
                            </label>
                            <div className="flex items-center gap-2 flex-wrap">
                              {watchersState && watchersState.length > 0 ? (
                                watchersState.map((watcher) => (
                                  <TooltipProvider key={watcher.id}>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <button 
                                          className="relative group"
                                          onClick={() => handleRemoveWatcher(watcher.id)}
                                        >
                                          <Avatar className="w-7 h-7 cursor-pointer hover:ring-2 hover:ring-red-300 transition-all">
                                            <AvatarFallback className="text-xs bg-slate-200 text-slate-700 group-hover:bg-red-100 group-hover:text-red-700">
                                              {getInitials(watcher.name)}
                                            </AvatarFallback>
                                          </Avatar>
                                          <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full text-white text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                            ×
                                          </span>
                                        </button>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p className="text-xs">{watcher.name}</p>
                                        <p className="text-xs text-slate-400">Click to remove</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                ))
                              ) : (
                                <span className="text-sm text-slate-500">No watchers</span>
                              )}
                              <Popover open={watcherPopoverOpen} onOpenChange={(open) => {
                                setWatcherPopoverOpen(open);
                                if (open) {
                                  setUserSearchQuery('');
                                }
                              }}>
                                <PopoverTrigger asChild>
                                  <Button variant="outline" size="sm" className="h-7 w-7 p-0 rounded-full hover:bg-blue-50 hover:border-blue-300">
                                    <Plus className="w-3 h-3" />
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-72 p-0" align="start">
                                  <Command shouldFilter={false}>
                                    <CommandInput 
                                      placeholder="Search users to add..." 
                                      value={userSearchQuery}
                                      onValueChange={setUserSearchQuery}
                                    />
                                    <CommandList>
                                      <CommandEmpty>
                                        {usersLoading ? 'Loading users...' : 'No users found.'}
                                      </CommandEmpty>
                                      <CommandGroup heading="Add Watcher">
                                        {filteredUsers.filter(user => !user.is_watcher).map((user) => (
                                          <CommandItem
                                            key={user.id}
                                            onSelect={() => handleAddWatcher(user)}
                                            className="flex items-center gap-2"
                                          >
                                            <Avatar className="w-6 h-6">
                                              <AvatarFallback className="text-xs bg-slate-200 text-slate-700">
                                                {getInitials(user.name)}
                                              </AvatarFallback>
                                            </Avatar>
                                            <div className="flex-1 min-w-0">
                                              <p className="text-sm font-medium truncate">{user.name}</p>
                                              <p className="text-xs text-slate-500 truncate">{user.email}</p>
                                            </div>
                                          </CommandItem>
                                        ))}
                                      </CommandGroup>
                                      {filteredUsers.filter(user => user.is_watcher).length > 0 && (
                                        <CommandGroup heading="Already Watching">
                                          {filteredUsers.filter(user => user.is_watcher).map((user) => (
                                            <CommandItem
                                              key={user.id}
                                              disabled
                                              className="flex items-center gap-2 opacity-50"
                                            >
                                              <Avatar className="w-6 h-6">
                                                <AvatarFallback className="text-xs bg-slate-200 text-slate-700">
                                                  {getInitials(user.name)}
                                                </AvatarFallback>
                                              </Avatar>
                                              <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium truncate">{user.name}</p>
                                                <p className="text-xs text-slate-500 truncate">{user.email}</p>
                                              </div>
                                              <Eye className="w-4 h-4 text-blue-500" />
                                            </CommandItem>
                                          ))}
                                        </CommandGroup>
                                      )}
                                    </CommandList>
                                  </Command>
                                </PopoverContent>
                              </Popover>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Financial Details - ALWAYS visible (view + edit mode) */}
                      <div className="bg-white rounded-lg p-4 border border-slate-200">
                        <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                          <DollarSign className="w-4 h-4" />
                          Financial Details
                        </h3>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="text-xs font-medium text-slate-500 block mb-1">Revenue Potential ($)</label>
                            {editing ? (
                              <Input
                                type="number"
                                value={editedData.revenue_potential_cents ? editedData.revenue_potential_cents / 100 : ''}
                                onChange={(e) => setEditedData({...editedData, revenue_potential_cents: e.target.value ? e.target.value * 100 : null})}
                                className="h-9"
                                placeholder="0"
                              />
                            ) : (
                              <p className="text-sm font-medium text-slate-900">
                                {opportunity.revenue_potential_cents 
                                  ? `$${(opportunity.revenue_potential_cents / 100).toLocaleString()}`
                                  : 'Not set'}
                              </p>
                            )}
                          </div>
                          <div>
                            <label className="text-xs font-medium text-slate-500 block mb-1">Confidence (%)</label>
                            {editing ? (
                              <Input
                                type="number"
                                min="0"
                                max="100"
                                value={editedData.confidence_percent || ''}
                                onChange={(e) => setEditedData({...editedData, confidence_percent: parseInt(e.target.value) || null})}
                                className="h-9"
                                placeholder="0"
                              />
                            ) : (
                              <p className="text-sm font-medium text-slate-900">
                                {opportunity.confidence_percent !== null && opportunity.confidence_percent !== undefined
                                  ? `${opportunity.confidence_percent}%`
                                  : 'Not set'}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Timeline - Editable */}
                      <div className="bg-white rounded-lg p-4 border border-slate-200">
                        <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2 mb-3">
                          <Calendar className="w-4 h-4" />
                          Program Timeline
                        </h3>
                        <div className="grid grid-cols-3 gap-4">
                          {[
                            { label: 'SOP Date', field: 'sop_date' },
                            { label: 'EOP Date', field: 'eop_date' },
                            { label: 'Target Award', field: 'target_award_date' },
                          ].map((item) => (
                            <div key={item.field}>
                              <label className="text-xs font-medium text-slate-500 block mb-1">{item.label}</label>
                              {editing ? (
                                <Input
                                  type="date"
                                  value={editedData[item.field] || ''}
                                  onChange={(e) => setEditedData({...editedData, [item.field]: e.target.value})}
                                  className="h-9"
                                />
                              ) : (
                                <p className="text-sm font-medium text-slate-900">
                                  {formatDate(opportunity[item.field])}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Location */}
                      <div className="bg-white rounded-lg p-4 border border-slate-200">
                        <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2 mb-3">
                          <Globe className="w-4 h-4" />
                          Location
                        </h3>
                        <div className="grid grid-cols-3 gap-4">
                          {[
                            { label: 'Region', value: opportunity.region },
                            { label: 'Country', value: opportunity.country },
                            { label: 'Plant', value: opportunity.plant_location },
                          ].map((item) => (
                            <div key={item.label}>
                              <label className="text-xs font-medium text-slate-500 block mb-1">{item.label}</label>
                              <p className="text-sm font-medium text-slate-900">
                                {item.value || 'Not set'}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Target Parts - Enhanced with multi-select from master list */}
                      <div className="bg-white rounded-lg p-4 border border-slate-200">
                          <div className="flex items-center justify-between mb-3">
                            <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                              <Package className="w-4 h-4" />
                              Target Parts
                            </h3>
                            {editing && masterPartTypes.length > 0 && (
                              <button
                                type="button"
                                onClick={() => setPartTypesExpanded(!partTypesExpanded)}
                                className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
                              >
                                {partTypesExpanded ? (
                                  <>
                                    <ChevronUp className="w-3 h-3" />
                                    Collapse
                                  </>
                                ) : (
                                  <>
                                    <ChevronDown className="w-3 h-3" />
                                    {editedData.target_parts?.length > 0 
                                      ? `Edit Selection (${editedData.target_parts.length})` 
                                      : 'Select Parts'}
                                  </>
                                )}
                              </button>
                            )}
                          </div>
                          
                          {editing ? (
                            <div className="space-y-3">
                              {/* Show selected parts as badges */}
                              {editedData.target_parts && editedData.target_parts.length > 0 && (
                                <div className="flex flex-wrap gap-2">
                                  {editedData.target_parts.map((part, idx) => (
                                    <Badge 
                                      key={idx} 
                                      variant="secondary"
                                      className="pr-1 flex items-center gap-1"
                                    >
                                      {part}
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setEditedData({
                                            ...editedData,
                                            target_parts: editedData.target_parts.filter(p => p !== part)
                                          });
                                        }}
                                        className="ml-1 hover:bg-slate-300 rounded-full p-0.5"
                                      >
                                        <X className="w-3 h-3" />
                                      </button>
                                    </Badge>
                                  ))}
                                </div>
                              )}
                              
                              {/* Part Type Selection Grid - Collapsible */}
                              {partTypesExpanded && masterPartTypes.length > 0 && (
                                <div className="border border-slate-200 rounded-lg p-3 bg-slate-50 space-y-2">
                                  <p className="text-xs text-slate-500 mb-2">
                                    Select from master part types list:
                                  </p>
                                  <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                                    {masterPartTypes.map((partType) => {
                                      const isSelected = editedData.target_parts?.includes(partType.name);
                                      return (
                                        <label
                                          key={partType.id}
                                          className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                                            isSelected 
                                              ? 'bg-blue-50 border border-blue-200' 
                                              : 'bg-white border border-slate-200 hover:bg-slate-50'
                                          }`}
                                        >
                                          <Checkbox
                                            checked={isSelected}
                                            onCheckedChange={(checked) => {
                                              const currentParts = editedData.target_parts || [];
                                              if (checked) {
                                                setEditedData({
                                                  ...editedData,
                                                  target_parts: [...currentParts, partType.name]
                                                });
                                              } else {
                                                setEditedData({
                                                  ...editedData,
                                                  target_parts: currentParts.filter(p => p !== partType.name)
                                                });
                                              }
                                            }}
                                          />
                                          <span className="text-sm text-slate-700">{partType.name}</span>
                                          {partType.code && (
                                            <span className="text-xs text-slate-400">({partType.code})</span>
                                          )}
                                        </label>
                                      );
                                    })}
                                  </div>
                                  {editedData.target_parts?.length > 0 && (
                                    <div className="flex justify-between items-center pt-2 border-t border-slate-200 mt-2">
                                      <span className="text-xs text-slate-500">
                                        {editedData.target_parts.length} selected
                                      </span>
                                      <button
                                        type="button"
                                        onClick={() => setEditedData({...editedData, target_parts: []})}
                                        className="text-xs text-red-600 hover:text-red-700"
                                      >
                                        Clear all
                                      </button>
                                    </div>
                                  )}
                                </div>
                              )}
                              
                              {/* Prompt to expand if nothing selected and collapsed */}
                              {!partTypesExpanded && (!editedData.target_parts || editedData.target_parts.length === 0) && masterPartTypes.length > 0 && (
                                <p className="text-xs text-slate-500 italic">
                                  Click &ldquo;Select Parts&rdquo; above to choose target part types for this opportunity.
                                </p>
                              )}
                              
                              {/* Fallback if no master part types exist */}
                              {masterPartTypes.length === 0 && (
                                <p className="text-xs text-amber-600">
                                  No part types defined. Add part types in Competitive Intelligence &gt; Part Types.
                                </p>
                              )}
                            </div>
                          ) : (
                            <div className="flex flex-wrap gap-2">
                              {opportunity.target_parts && opportunity.target_parts.length > 0 ? (
                                opportunity.target_parts.map((part, idx) => (
                                  <Badge key={idx} variant="secondary">
                                    {part}
                                  </Badge>
                                ))
                              ) : (
                                <p className="text-sm text-slate-500">No target parts specified</p>
                              )}
                            </div>
                          )}
                        </div>

                      {/* Custom Fields */}
                      <CustomFieldsSection entityType="opportunity" entityId={opportunityId} />

                      {/* Workflows Panel */}
                      <EntityWorkflowsPanel
                        entityType="opportunity"
                        entityId={opportunityId}
                        entityName={opportunity?.program_name || opportunityId}
                      />

                      {/* Notes */}
                      <div className="bg-white rounded-lg p-4 border border-slate-200">
                        <h3 className="text-sm font-semibold text-slate-900 mb-3">Notes</h3>
                        {editing ? (
                          <textarea
                            value={editedData.notes || ''}
                            onChange={(e) => setEditedData({...editedData, notes: e.target.value})}
                            rows={4}
                            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Add notes..."
                          />
                        ) : (
                          <p className="text-sm text-slate-700 whitespace-pre-wrap">
                            {opportunity.notes || 'No notes'}
                          </p>
                        )}
                      </div>

                      {/* Competitive Landscape */}
                      <div className="bg-white rounded-lg p-4 border border-slate-200">
                          <h3 className="text-sm font-semibold text-slate-900 mb-3">Competitive Landscape</h3>
                          {editing ? (
                            <textarea
                              value={editedData.competitive_landscape || ''}
                              onChange={(e) => setEditedData({...editedData, competitive_landscape: e.target.value})}
                              rows={3}
                              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="Describe competitors, their strengths, our position..."
                            />
                          ) : (
                            <p className="text-sm text-slate-700 whitespace-pre-wrap">
                              {opportunity.competitive_landscape || 'No competitive analysis yet'}
                            </p>
                          )}
                        </div>
                    </TabsContent>

                    {/* Parts Tab */}
                    <TabsContent value="parts" className="m-0">
                      <OpportunityParts 
                        opportunityId={opportunityId}
                        opportunity={opportunity}
                        onRevenueUpdate={(data) => {
                          setOpportunity(prev => ({
                            ...prev,
                            revenue_potential_cents: data.total_revenue_cents,
                            revenue_source: 'calculated'
                          }));
                          if (onUpdate) onUpdate();
                        }}
                      />
                    </TabsContent>

                    {/* Market Data Tab */}
                    <TabsContent value="ihs-data" className="m-0">
                      <LinkedIHSCarlines 
                        opportunityId={opportunityId}
                        onOpenBrowser={() => setShowIHSBrowser(true)}
                      />
                    </TabsContent>

                    {/* Documents Tab */}
                    <TabsContent value="documents" className="m-0">
                      <OpportunityDocuments opportunityId={opportunityId} />
                    </TabsContent>

                    {/* Comments Tab */}
                    <TabsContent value="comments" className="m-0">
                      <OpportunityComments opportunityId={opportunityId} />
                    </TabsContent>

                    {/* Activity Tab */}
                    <TabsContent value="activity" className="m-0">
                      <div className="space-y-4">
                        <h3 className="text-sm font-semibold text-slate-900">Activity Timeline</h3>
                        {opportunity.activities && opportunity.activities.length > 0 ? (
                          <div className="space-y-4">
                            {opportunity.activities.map((activity) => (
                              <div key={activity.id} className="flex gap-3">
                                <div className="flex-shrink-0">
                                  <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                                    <Activity className="w-4 h-4 text-slate-500" />
                                  </div>
                                </div>
                                <div className="flex-1 min-w-0 pb-4 border-l border-slate-200 pl-4 -ml-4">
                                  <p className="text-sm text-slate-900">{activity.description}</p>
                                  <p className="text-xs text-slate-500 mt-1">
                                    {activity.created_by?.name || 'System'} - {formatDate(activity.created_at)}
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-slate-500 text-center py-8">
                            No activities yet
                          </p>
                        )}
                      </div>
                    </TabsContent>
                  </Tabs>
                </div>
              </ScrollArea>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-slate-500">Opportunity not found</div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* IHS Browser Modal */}
      {showIHSBrowser && (
        <IHSCarlineBrowser
          opportunityId={opportunityId}
          onLink={(carline) => {
            loadOpportunity();
            alert(`Successfully linked ${carline.production_nameplate}`);
          }}
          onClose={() => setShowIHSBrowser(false)}
          onCreateOpportunity={(result) => {
            setShowIHSBrowser(false);
            alert(`Created opportunity: ${result.message}`);
            onUpdate && onUpdate();
          }}
        />
      )}
      <ConfirmActionDialog
        open={promoteConfirmOpen}
        onOpenChange={setPromoteConfirmOpen}
        title={hasProgramConnection ? 'Connect to suggested Program 360?' : 'Create Program 360 from this opportunity?'}
        description="This turns the Pipeline pursuit into the managed Program 360 workflow."
        items={[
          hasProgramConnection
            ? `${opportunity?.program_name || 'This opportunity'} will be connected to ${programConnection.program_name || 'the suggested Program 360 record'}.`
            : `${opportunity?.program_name || 'This opportunity'} will create a new Program 360 record.`,
          'Linked market forecast records will become the Program 360 market basis for S&OP.',
          `${programConnection.linked_parts_count || opportunity?.linked_parts_count || 0} linked part${(programConnection.linked_parts_count || opportunity?.linked_parts_count || 0) === 1 ? '' : 's'} may be attached where safe.`,
          opportunity?.target_parts?.length
            ? `${opportunity.target_parts.length} selected target part${opportunity.target_parts.length === 1 ? '' : 's'} will become draft Program 360 part records if they are not already linked.`
            : 'Selected target parts can still be added manually after Program 360 is created.',
          'The saved action will appear in Program 360 Activity.'
        ]}
        confirmLabel={hasProgramConnection ? 'Connect Program 360' : 'Create Program 360'}
        onConfirm={() => {
          setPromoteConfirmOpen(false);
          handlePromoteToProgram();
        }}
      />
    </>
  );
};

export default OpportunityDetailPanel;
